import React, { createContext, useContext, useReducer, useMemo } from 'react';

// ============================================
// PARTICLE DATABASE (Enhanced with physics info)
// ============================================
export const PARTICLES = {
    proton: {
        type: 'proton',
        name: 'Proton (p)',
        symbol: 'p',
        mass: 0.93827,
        massUnit: '938.27 MeV/c²',
        lifetime: Infinity,
        lifetimeUnit: 'Stable (>10³⁴ years)',
        charge: 1,
        color: '#3498DB',
        decayMode: 'None (stable)',
        composition: 'uud (2 up + 1 down)',
        description: 'Primary beam particle for MCS tomography. Non-decaying, ideal for scattering measurements.',
        funFact: 'Proton stability is protected by baryon number conservation.'
    },
    pion: {
        type: 'pion',
        name: 'Pion (π⁺)',
        symbol: 'π⁺',
        mass: 0.13957,
        massUnit: '139.57 MeV/c²',
        lifetime: 2.6033e-8,
        lifetimeUnit: '26.0 ns',
        charge: 1,
        color: '#E74C3C',
        decayMode: 'μ⁺ + νμ (99.99%)',
        composition: 'u + d̄ (up + anti-down)',
        description: 'Lightest meson. Can contaminate proton beams.',
        funFact: 'Discovered in 1947 by Cecil Powell using cosmic rays.'
    },
    kaon: {
        type: 'kaon',
        name: 'Kaon (K⁺)',
        symbol: 'K⁺',
        mass: 0.49367,
        massUnit: '493.68 MeV/c²',
        lifetime: 1.2380e-8,
        lifetimeUnit: '12.4 ns',
        charge: 1,
        color: '#9B59B6',
        decayMode: 'μ⁺νμ (63%), π⁺π⁰ (21%)',
        composition: 'u + s̄ (up + anti-strange)',
        description: 'Contains strange quark. Rare in low-momentum beams.',
        funFact: 'K⁰ mixing led to the discovery of CP violation (Nobel 1980).'
    },
    muon: {
        type: 'muon',
        name: 'Muon (μ⁺)',
        symbol: 'μ⁺',
        mass: 0.10566,
        massUnit: '105.66 MeV/c²',
        lifetime: 2.1969e-6,
        lifetimeUnit: '2.20 μs',
        charge: 1,
        color: '#2ECC71',
        decayMode: 'e⁺ + νₑ + ν̄μ (100%)',
        composition: 'Fundamental lepton (no quarks)',
        description: 'Heavy electron. Product of pion decay.',
        funFact: 'Muon g-2 anomaly hints at new physics beyond Standard Model!'
    },
    electron: {
        type: 'electron',
        name: 'Electron (e⁻)',
        symbol: 'e⁻',
        mass: 0.000511,
        massUnit: '0.511 MeV/c²',
        lifetime: Infinity,
        lifetimeUnit: 'Stable',
        charge: -1,
        color: '#00BCD4',
        decayMode: 'None (stable)',
        composition: 'Fundamental lepton',
        description: 'Lightest charged lepton. Minimal MCS due to low mass.',
        funFact: 'First subatomic particle discovered (J.J. Thomson, 1897).'
    },
    carbon: {
        type: 'carbon',
        name: 'Carbon Ion (¹²C⁶⁺)',
        symbol: '¹²C',
        mass: 11.178, // GeV/c² (12 * 0.93149 atomic mass units)
        massUnit: '11.18 GeV/c²',
        lifetime: Infinity,
        lifetimeUnit: 'Stable',
        charge: 6, // Fully stripped: q = 6e
        color: '#27AE60',
        decayMode: 'None (stable nucleus)',
        composition: 'Nucleus (6p + 6n)',
        description: 'Heavy ion for cancer therapy. Sharp Bragg peak and high biological effectiveness.',
        funFact: 'Carbon ions deliver 3�- more biological damage to tumors than protons.'
    },
};

// ============================================
// MATERIAL DATABASE (for MCS tomography)
// ============================================
export const MATERIALS = {
    air: {
        type: 'air',
        name: 'Air',
        formula: 'N₂/O₂',
        X0: 30420, // radiation length in cm (very large = minimal scattering)
        density: 0.00125, // g/cm³
        zEff: 7.3, // effective atomic number
        color: '#E8F4FD',
        description: 'Standard atmosphere. Almost no scattering.',
        applications: 'Baseline measurements, drift regions.'
    },
    pvc: {
        type: 'pvc',
        name: 'PVC',
        formula: '(C₂H₃Cl)ₙ',
        X0: 15.8, // radiation length in cm
        density: 1.40, // g/cm³
        zEff: 12.4, // effective atomic number
        color: '#9B59B6',
        description: 'Polyvinyl chloride. High Z due to chlorine content.',
        applications: 'Pipes, construction materials, medical tubing.'
    },
    delrin: {
        type: 'delrin',
        name: 'Delrin',
        formula: '(CH₂O)ₙ',
        X0: 28.7, // radiation length in cm
        density: 1.41, // g/cm³
        zEff: 7.0, // effective atomic number
        color: '#1ABC9C',
        description: 'Polyoxymethylene (POM). Low Z, organic polymer.',
        applications: 'Gears, bearings, precision parts.'
    },
    pmma: {
        type: 'pmma',
        name: 'PMMA (Acrylic)',
        formula: '(C₅H₈O₂)ₙ',
        X0: 34.4, // radiation length in cm
        density: 1.18, // g/cm³
        zEff: 6.5, // effective atomic number
        color: '#3498DB',
        description: 'Polymethyl methacrylate. Tissue-equivalent material.',
        applications: 'Medical phantoms, optical components.'
    },
    aluminum: {
        type: 'aluminum',
        name: 'Aluminum',
        formula: 'Al',
        X0: 8.9, // radiation length in cm
        density: 2.70, // g/cm³
        zEff: 13, // atomic number
        color: '#95A5A6',
        description: 'Light metal. Standard calibration material.',
        applications: 'Structural components, beam windows.'
    },
    water: {
        type: 'water',
        name: 'Water',
        formula: 'H₂O',
        X0: 36.1, // radiation length in cm
        density: 1.00, // g/cm³
        zEff: 7.4, // effective atomic number
        color: '#5DADE2',
        description: 'Tissue equivalent. Standard medical phantom material.',
        applications: 'Medical imaging calibration.'
    },
    bone: {
        type: 'bone',
        name: 'Bone (Cortical)',
        formula: 'Ca₁₀(PO₄)₆(OH)₂',
        X0: 10.4, // radiation length in cm
        density: 1.85, // g/cm³
        zEff: 13.8, // effective atomic number
        color: '#F5DEB3',
        description: 'Cortical bone composition. High contrast in proton CT.',
        applications: 'Medical imaging, dosimetry.'
    },
    lead: {
        type: 'lead',
        name: 'Lead',
        formula: 'Pb',
        X0: 0.56, // radiation length in cm (very short!)
        density: 11.35, // g/cm³
        zEff: 82, // atomic number
        color: '#2C3E50',
        description: 'High-Z metal. Extreme scattering, used for shielding.',
        applications: 'Radiation shielding, calorimeter absorber.'
    },
    iron: {
        type: 'iron',
        name: 'Iron/Steel',
        formula: 'Fe',
        X0: 1.76, // radiation length in cm
        density: 7.87, // g/cm³
        zEff: 26, // atomic number
        color: '#7F8C8D',
        description: 'Standard metal. Hadronic calorimeter absorber.',
        applications: 'Muon filters, hadronic calorimeters.'
    },
    copper: {
        type: 'copper',
        name: 'Copper',
        formula: 'Cu',
        X0: 1.44, // radiation length in cm
        density: 8.96, // g/cm³
        zEff: 29, // atomic number
        color: '#E67E22',
        description: 'Excellent conductor. Used in EM calorimeters.',
        applications: 'Calorimeters, beam dumps, electronics.'
    },
    scintillator: {
        type: 'scintillator',
        name: 'Plastic Scintillator',
        formula: 'C₁₀H₁₁',
        X0: 42.5, // radiation length in cm
        density: 1.03, // g/cm³
        zEff: 5.7, // effective atomic number
        color: '#00D4AA',
        description: 'Polyvinyltoluene-based. Emits light when particles pass.',
        applications: 'Particle detection, timing, triggering.'
    },
    tungsten: {
        type: 'tungsten',
        name: 'Tungsten',
        formula: 'W',
        X0: 0.35, // radiation length in cm (shortest common!)
        density: 19.3, // g/cm³
        zEff: 74, // atomic number
        color: '#34495E',
        description: 'Highest melting point metal. Compact calorimeters.',
        applications: 'Compact calorimeters, collimators, beam dumps.'
    },
    silicon: {
        type: 'silicon',
        name: 'Silicon',
        formula: 'Si',
        X0: 9.37, // radiation length in cm
        density: 2.33, // g/cm³
        zEff: 14, // atomic number
        color: '#5D6D7E',
        description: 'Semiconductor. Basis of all tracking detectors.',
        applications: 'Pixel/strip detectors, tracking systems.'
    },
    carbonfiber: {
        type: 'carbonfiber',
        name: 'Carbon Fiber',
        formula: 'C',
        X0: 25.7, // radiation length in cm
        density: 1.75, // g/cm³
        zEff: 6, // atomic number
        color: '#1C2833',
        description: 'Strong and light. Minimal scattering for support.',
        applications: 'Detector support structures, beam pipes.'
    }
};

import { C_M_PER_NS, RIGIDITY_FACTOR } from '../utils/constants.js';

// ============================================
// PHYSICS CALCULATIONS  -  re-exported from physicsMath.js (single source of truth)
// ============================================
export {
    calculateKinematics,
    calculateMCSAngle,
    calculateMCSVariance,
    calculateDecayLength,
    calculateLabLifetime,
} from '../utils/physicsMath.js';

import {
    calculateKinematics,
    calculateMCSAngle,
    calculateMCSVariance,
    calculateDecayLength,
    calculateLabLifetime,
} from '../utils/physicsMath.js';

// ============================================
// INITIAL STATE
// ============================================
const createInitialState = () => {
    const particle = PARTICLES.proton;
    const material = MATERIALS.pvc;
    const momentum = 0.7; // GeV/c (Z-SCANNER default)
    const thickness = 2.0; // cm (phantom thickness)
    const { energy, beta, gamma } = calculateKinematics(momentum, particle.mass);
    const decayLength = calculateDecayLength(beta, gamma, particle.lifetime);
    const labLifetime = calculateLabLifetime(gamma, particle.lifetime);
    const thetaRMS = calculateMCSAngle(momentum, particle.mass, thickness, material.X0);

    return {
        // Particle selection
        particle,

        // Material selection (for MCS)
        material: 'pvc',
        insertMaterial: null, // Optional insert with different material
        phantomThickness: thickness, // cm
        phantomSize: 3, // cm (3x3 cm phantom)

        // Beam parameters
        beam: {
            momentum,
            intensity: 10000,
        },

        // Beamline geometry
        beamline: {
            length: 0.2, // meters (20 cm for MCS setup)
        },

        // Active detectors/trackers
        detectors: {
            upstreamTracker1: true,
            upstreamTracker2: true,
            phantom: true,
            downstreamTracker1: true,
            downstreamTracker2: true,
        },

        // Physics settings
        physics: {
            decayEnabled: false, // Protons don't decay
            mcsEnabled: true,    // MCS physics enabled
            magneticField: 0,    // Tesla
            showSecondaries: false,
        },

        // Analysis settings
        analysis: {
            mode: 'mcs', // 'mcs', 'decay', 'single', 'continuous'
            targetEvents: 10000,
            showErrorBars: true,
            fitCurve: true,
            normalize: true,
        },

        // RNG seed for reproducibility
        rngSeed: Math.floor(Date.now() / 1000),

        // Detector response (toy model)
        detectorResponse: {
            enabled: true,
            efficiency: 0.98,
            angularResolution: 0.1, // mrad
            positionResolution: 100e-6, // 100 μm
        },

        // Derived physics values (auto-calculated)
        derived: {
            energy,
            beta,
            gamma,
            decayLength,
            labLifetime,
            expectedSurvival: particle.lifetime === Infinity ? 1 : Math.exp(-0.2 / decayLength),
            mcsAngle: thetaRMS * 1000, // mrad
            thetaRMS,
            thetaRMSmrad: thetaRMS * 1000,
            variance: thetaRMS * thetaRMS,
        },

        // Simulation state
        simulation: {
            running: false,
            progress: 0,
            results: null,
            error: null,
        },

        // Active preset (for UI highlighting)
        activePreset: 'z-scanner-pvc',

        // Analysis mode (determines sidebar, physics engine, results display)
        analysisMode: 'mcs',
    };
};

// ============================================
// ACTION TYPES
// ============================================
const ActionTypes = {
    SET_PARTICLE: 'SET_PARTICLE',
    SET_MATERIAL: 'SET_MATERIAL',
    SET_INSERT_MATERIAL: 'SET_INSERT_MATERIAL',
    SET_PHANTOM_THICKNESS: 'SET_PHANTOM_THICKNESS',
    SET_PHANTOM_SIZE: 'SET_PHANTOM_SIZE',
    SET_MOMENTUM: 'SET_MOMENTUM',
    SET_BEAM_INTENSITY: 'SET_BEAM_INTENSITY',
    SET_BEAMLINE_LENGTH: 'SET_BEAMLINE_LENGTH',
    TOGGLE_DETECTOR: 'TOGGLE_DETECTOR',
    SET_PHYSICS_DECAY: 'SET_PHYSICS_DECAY',
    SET_PHYSICS_MCS: 'SET_PHYSICS_MCS',
    SET_MAGNETIC_FIELD: 'SET_MAGNETIC_FIELD',
    SET_ANALYSIS_MODE: 'SET_ANALYSIS_MODE',
    SET_TARGET_EVENTS: 'SET_TARGET_EVENTS',
    TOGGLE_ANALYSIS_OPTION: 'TOGGLE_ANALYSIS_OPTION',
    SET_RNG_SEED: 'SET_RNG_SEED',
    SET_DETECTOR_RESPONSE: 'SET_DETECTOR_RESPONSE',
    LOAD_PRESET: 'LOAD_PRESET',
    START_SIMULATION: 'START_SIMULATION',
    UPDATE_PROGRESS: 'UPDATE_PROGRESS',
    COMPLETE_SIMULATION: 'COMPLETE_SIMULATION',
    SIMULATION_ERROR: 'SIMULATION_ERROR',
    RESET_SIMULATION: 'RESET_SIMULATION',
};

// ============================================
// REDUCER
// ============================================
const recalculateDerived = (state) => {
    const { beta, gamma, energy } = calculateKinematics(
        state.beam.momentum,
        state.particle.mass
    );
    const decayLength = calculateDecayLength(beta, gamma, state.particle.lifetime);
    const labLifetime = calculateLabLifetime(gamma, state.particle.lifetime);
    const expectedSurvival = state.particle.lifetime === Infinity
        ? 1
        : Math.exp(-state.beamline.length / decayLength);
    
    // MCS calculations - only compute when material exists and phantom has thickness
    const mat = MATERIALS[state.material];
    const charge = state.particle?.charge || 1;
    const thetaRMS = (mat && state.phantomThickness > 0)
        ? calculateMCSAngle(state.beam.momentum, state.particle.mass, state.phantomThickness, mat.X0, charge)
        : 0;

    // TOF: time of flight in nanoseconds
    const tof = (state.beamline?.length && beta > 0)
        ? state.beamline.length / (beta * C_M_PER_NS)
        : 0;

    // Spectroscopy: bending radius in metres
    const B = state.physics?.magneticField || 0;
    const absCharge = Math.abs(charge) || 1;
    const bendingRadius = (B > 0)
        ? state.beam.momentum / (RIGIDITY_FACTOR * B * absCharge)
        : Infinity;

    // Medical mode: approximate stopping range (cm) for protons/ions in water
    // Uses empirical power-law R ∝ (T/A)^1.77 with T = kinetic energy per nucleon
    // Source: Bortfeld approximation.  Only valid for protons / light ions.
    const kineticEnergyMeV = (energy - state.particle.mass) * 1000; // MeV
    const massNumber = Math.round(state.particle.mass / 0.931); // approximate A
    const TperA = massNumber > 0 ? kineticEnergyMeV / massNumber : kineticEnergyMeV;
    const stoppingRange = TperA > 0 ? 0.0022 * Math.pow(TperA, 1.77) : 0; // cm in water

    return {
        ...state,
        derived: {
            energy,
            beta,
            gamma,
            decayLength,
            labLifetime,
            expectedSurvival,
            mcsAngle: thetaRMS * 1000, // mrad
            thetaRMS,
            thetaRMSmrad: thetaRMS * 1000,
            variance: thetaRMS * thetaRMS,
            tof,
            bendingRadius,
            stoppingRange,
        }
    };
};

const simulationReducer = (state, action) => {
    let newState;

    switch (action.type) {
        case ActionTypes.SET_PARTICLE:
            newState = {
                ...state,
                particle: PARTICLES[action.payload],
                activePreset: null, // Clear preset when manually changing
            };
            return recalculateDerived(newState);

        case ActionTypes.SET_MATERIAL:
            newState = {
                ...state,
                material: action.payload, // Store as string key
                activePreset: null,
            };
            return recalculateDerived(newState);

        case ActionTypes.SET_INSERT_MATERIAL:
            newState = {
                ...state,
                insertMaterial: action.payload, // Store as string key or null
                activePreset: null,
            };
            return recalculateDerived(newState);

        case ActionTypes.SET_PHANTOM_THICKNESS:
            newState = {
                ...state,
                phantomThickness: action.payload,
                activePreset: null,
            };
            return recalculateDerived(newState);

        case ActionTypes.SET_PHANTOM_SIZE:
            newState = {
                ...state,
                phantomSize: action.payload,
                activePreset: null,
            };
            return recalculateDerived(newState);

        case ActionTypes.SET_MOMENTUM:
            newState = {
                ...state,
                beam: { ...state.beam, momentum: action.payload },
                activePreset: null,
            };
            return recalculateDerived(newState);

        case ActionTypes.SET_BEAM_INTENSITY:
            newState = {
                ...state,
                beam: { ...state.beam, intensity: action.payload },
            };
            return recalculateDerived(newState);

        case ActionTypes.SET_BEAMLINE_LENGTH:
            newState = {
                ...state,
                beamline: { ...state.beamline, length: action.payload },
                activePreset: null,
            };
            return recalculateDerived(newState);

        case ActionTypes.TOGGLE_DETECTOR:
            return {
                ...state,
                detectors: {
                    ...state.detectors,
                    [action.payload]: !state.detectors[action.payload],
                },
                activePreset: null,
            };

        case ActionTypes.SET_PHYSICS_DECAY:
            return {
                ...state,
                physics: { ...state.physics, decayEnabled: action.payload },
            };

        case ActionTypes.SET_PHYSICS_MCS:
            return {
                ...state,
                physics: { ...state.physics, mcsEnabled: action.payload },
            };

        case ActionTypes.SET_MAGNETIC_FIELD:
            return {
                ...state,
                physics: { ...state.physics, magneticField: action.payload },
                activePreset: null,
            };

        case ActionTypes.SET_ANALYSIS_MODE:
            return {
                ...state,
                analysis: { ...state.analysis, mode: action.payload },
            };

        case ActionTypes.SET_TARGET_EVENTS:
            return {
                ...state,
                analysis: { ...state.analysis, targetEvents: action.payload },
            };

        case ActionTypes.TOGGLE_ANALYSIS_OPTION:
            return {
                ...state,
                analysis: {
                    ...state.analysis,
                    [action.payload]: !state.analysis[action.payload],
                },
            };

        case ActionTypes.SET_RNG_SEED:
            return {
                ...state,
                rngSeed: action.payload,
            };

        case ActionTypes.SET_DETECTOR_RESPONSE:
            return {
                ...state,
                detectorResponse: action.payload,
            };

        case ActionTypes.LOAD_PRESET:
            const preset = action.payload;
            newState = {
                ...state,
                particle: PARTICLES[preset.particle] || PARTICLES.proton,
                material: preset.material || 'pvc',
                insertMaterial: preset.insertMaterial || null,
                phantomThickness: preset.phantomThickness ?? 2.0,
                phantomSize: preset.phantomSize ?? 3,
                beam: {
                    momentum: preset.momentum ?? 0.7,
                    intensity: preset.beamIntensity ?? 10000,
                },
                beamline: {
                    length: preset.beamlineLength ?? 0.2,
                },
                detectors: preset.detectors ? { ...preset.detectors } : state.detectors,
                physics: {
                    decayEnabled: preset.physics?.decayEnabled ?? false,
                    magneticField: preset.physics?.magneticField ?? state.physics.magneticField,
                    showSecondaries: preset.physics?.showSecondaries ?? false,
                    mcsEnabled: preset.physics?.mcsEnabled ?? true,
                },
                activePreset: preset.id,
                analysisMode: preset.analysisMode || 'mcs',
            };
            return recalculateDerived(newState);

        case ActionTypes.START_SIMULATION:
            return {
                ...state,
                simulation: {
                    running: true,
                    progress: 0,
                    results: null,
                    error: null,
                },
            };

        case ActionTypes.UPDATE_PROGRESS:
            return {
                ...state,
                simulation: {
                    ...state.simulation,
                    progress: action.payload,
                },
            };

        case ActionTypes.COMPLETE_SIMULATION:
            return {
                ...state,
                simulation: {
                    running: false,
                    progress: 100,
                    results: action.payload,
                    error: null,
                },
            };

        case ActionTypes.SIMULATION_ERROR:
            return {
                ...state,
                simulation: {
                    running: false,
                    progress: 0,
                    results: null,
                    error: action.payload,
                },
            };

        case ActionTypes.RESET_SIMULATION:
            return {
                ...state,
                simulation: {
                    running: false,
                    progress: 0,
                    results: null,
                    error: null,
                },
            };

        default:
            return state;
    }
};

// ============================================
// CONTEXT
// ============================================
const SimulationContext = createContext(null);

export const SimulationProvider = ({ children }) => {
    const [state, dispatch] = useReducer(simulationReducer, null, createInitialState);

    // Memoized action creators
    const actions = useMemo(() => ({
        setParticle: (type) => dispatch({ type: ActionTypes.SET_PARTICLE, payload: type }),
        setMaterial: (type) => dispatch({ type: ActionTypes.SET_MATERIAL, payload: type }),
        setInsertMaterial: (type) => dispatch({ type: ActionTypes.SET_INSERT_MATERIAL, payload: type }),
        setPhantomThickness: (t) => dispatch({ type: ActionTypes.SET_PHANTOM_THICKNESS, payload: t }),
        setPhantomSize: (s) => dispatch({ type: ActionTypes.SET_PHANTOM_SIZE, payload: s }),
        setMomentum: (p) => dispatch({ type: ActionTypes.SET_MOMENTUM, payload: p }),
        setBeamIntensity: (n) => dispatch({ type: ActionTypes.SET_BEAM_INTENSITY, payload: n }),
        setBeamlineLength: (l) => dispatch({ type: ActionTypes.SET_BEAMLINE_LENGTH, payload: l }),
        toggleDetector: (name) => dispatch({ type: ActionTypes.TOGGLE_DETECTOR, payload: name }),
        setDecayEnabled: (enabled) => dispatch({ type: ActionTypes.SET_PHYSICS_DECAY, payload: enabled }),
        setMCSEnabled: (enabled) => dispatch({ type: ActionTypes.SET_PHYSICS_MCS, payload: enabled }),
        setMagneticField: (B) => dispatch({ type: ActionTypes.SET_MAGNETIC_FIELD, payload: B }),
        setAnalysisMode: (mode) => dispatch({ type: ActionTypes.SET_ANALYSIS_MODE, payload: mode }),
        setTargetEvents: (n) => dispatch({ type: ActionTypes.SET_TARGET_EVENTS, payload: n }),
        toggleAnalysisOption: (opt) => dispatch({ type: ActionTypes.TOGGLE_ANALYSIS_OPTION, payload: opt }),
        setRngSeed: (seed) => dispatch({ type: ActionTypes.SET_RNG_SEED, payload: seed }),
        setDetectorResponse: (config) => dispatch({ type: ActionTypes.SET_DETECTOR_RESPONSE, payload: config }),
        loadPreset: (preset) => dispatch({ type: ActionTypes.LOAD_PRESET, payload: preset }),
        startSimulation: () => dispatch({ type: ActionTypes.START_SIMULATION }),
        updateProgress: (p) => dispatch({ type: ActionTypes.UPDATE_PROGRESS, payload: p }),
        completeSimulation: (results) => dispatch({ type: ActionTypes.COMPLETE_SIMULATION, payload: results }),
        simulationError: (err) => dispatch({ type: ActionTypes.SIMULATION_ERROR, payload: err }),
        resetSimulation: () => dispatch({ type: ActionTypes.RESET_SIMULATION }),
    }), []);

    const value = useMemo(() => ({ state, actions, dispatch }), [state, actions]);

    return (
        <SimulationContext.Provider value={value}>
            {children}
        </SimulationContext.Provider>
    );
};

// ============================================
// HOOK
// ============================================
export const useSimulationContext = () => {
    const context = useContext(SimulationContext);
    if (!context) {
        throw new Error('useSimulationContext must be used within a SimulationProvider');
    }
    return context;
};

export default SimulationContext;
